import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import { Card, IconCircle } from '@/components/ui';
import { cachedToday, getToday } from '@/lib/api';
import type { DogsBlock, DogsLine } from '@/lib/api';
import { todayISO } from '@/lib/dates';
import { dogsLineHref, dogsLineText, hasDogs } from '@/lib/dogs';

// The Dogs lines from /api/me/today's comingUp.dogs: who has the dogs
// today, then what's next (stays, appointments, the next day of each
// routine, trips still needing care). One component for Today's Dogs card
// and the Dogs page, so the two agree. The Worker picks the lines
// (coming-up-core.ts buildDogs); lib/dogs.ts words them.

function DogRow({ line, away }: { line: DogsLine; away: boolean }) {
  const t = dogsLineText(line, away);
  const { href, external } = dogsLineHref(line);
  const cls = away ? 'row is-current' : 'row';
  const body = (
    <>
      <IconCircle icon="paw" tone={away ? 'accent' : line.type === 'care' ? 'warn' : 'neutral'} iconSize={18} />
      <span className="row-body">
        <span className="row-title">{t.title}</span>
        <span className="row-meta is-wrap">
          {t.meta}
          {t.note && <span className={t.tone === 'warn' ? 'block-note is-warn' : 'block-note'}> · {t.note}</span>}
        </span>
      </span>
    </>
  );
  return external ? (
    <a className={cls} href={href} target="_blank" rel="noopener noreferrer">
      {body}
      <Icon name="external" size={16} className="row-chevron" />
    </a>
  ) : (
    <Link className={cls} to={href}>
      {body}
      <Icon name="chevronRight" size={18} className="row-chevron" />
    </Link>
  );
}

/** The rows, for inside a Card. */
export function DogLines({ dogs }: { dogs: DogsBlock }) {
  return (
    <>
      {dogs.away && <DogRow line={dogs.away} away />}
      {dogs.next.map((line) => (
        <DogRow key={line.key} line={line} away={false} />
      ))}
    </>
  );
}

/** Today's Dogs card; nothing when the dogs are home with nothing next. */
export function DogsCard({ dogs }: { dogs: DogsBlock | null | undefined }) {
  if (!hasDogs(dogs)) return null;
  return (
    <Card>
      <div className="card-head">
        <h2 className="card-title">Dogs</h2>
      </div>
      <DogLines dogs={dogs} />
    </Card>
  );
}

/** comingUp.dogs from /api/me/today, starting from Today's last copy. */
export function useDogsBlock(): DogsBlock | null {
  const [dogs, setDogs] = useState<DogsBlock | null>(() => cachedToday()?.comingUp?.dogs ?? null);
  useEffect(() => {
    let live = true;
    getToday(todayISO())
      .then((r) => {
        if (live) setDogs(r.comingUp?.dogs ?? null);
      })
      // The Dogs page still has the dogs' own pages without these.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return dogs;
}
